import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Loader2, ShieldCheck, Sparkles, Upload } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PLACEHOLDER = `RELIANCE,50,2400
TCS,30,3500
HDFCBANK,80,1600
INFY,75,1450
ITC,200,420`;

/**
 * Public Health Check landing page. Unauthenticated.
 *
 * The pitch: drop your portfolio, get a six-analyst memo per stock
 * + a portfolio-level concentration / regime / SELL-or-TRIM summary.
 * Free. No signup required. Designed as the marketing top-of-funnel
 * that converts curious retail users into Protect / Portfolio paying
 * users and warm-introduces RIAs / family offices.
 */
export default function HealthCheckUploadPage() {
  const navigate = useNavigate();
  const [rawText, setRawText] = useState('');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [rejected, setRejected] = useState([]);

  const submit = async () => {
    setError(null);
    setRejected([]);
    if (!rawText.trim()) {
      setError('Paste your holdings (one per line) — e.g. RELIANCE,50,2400');
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/healthcheck/upload`, {
        raw_text: rawText,
        submitted_email: email || undefined,
        submitted_name: name || undefined,
      });
      if ((data.rejected_rows || []).length) setRejected(data.rejected_rows);
      navigate(`/healthcheck/${data.public_token}`);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Upload failed';
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-slate-100">
      <div className="max-w-3xl mx-auto px-6 py-16">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 rounded-full bg-emerald-500/10 px-4 py-1.5 text-xs font-medium text-emerald-300 mb-5">
            <ShieldCheck className="w-3.5 h-3.5" />
            FREE PORTFOLIO HEALTH CHECK
          </div>
          <h1 className="text-4xl font-bold tracking-tight">
            What does an institutional committee say about <span className="text-emerald-400">your portfolio?</span>
          </h1>
          <p className="mt-4 text-slate-400 max-w-xl mx-auto">
            Six AI analysts debate every holding. Risk panel + CIO arbitration. Verifiable
            track record per stock. Get a complete portfolio audit in minutes.
          </p>
        </div>

        <div className="rounded-2xl border border-slate-800 bg-slate-900/60 backdrop-blur p-6 shadow-2xl">
          <label className="block text-sm font-semibold text-slate-300 mb-2">
            Paste your holdings
          </label>
          <p className="text-xs text-slate-500 mb-3">
            Format: <span className="font-mono text-slate-400">SYMBOL,QTY,AVG_COST</span> per line.
            We accept NSE symbols or ISINs. Up to 25 holdings per audit.
          </p>
          <textarea
            value={rawText}
            onChange={(e) => setRawText(e.target.value)}
            placeholder={PLACEHOLDER}
            rows={10}
            className="w-full font-mono text-sm rounded-lg bg-slate-950 border border-slate-800 px-4 py-3 text-slate-200 placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
            <input
              type="text"
              placeholder="Your name (optional)"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="rounded-lg bg-slate-950 border border-slate-800 px-4 py-2.5 text-sm text-slate-200 placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
            />
            <input
              type="email"
              placeholder="Email (optional, to receive PDF)"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="rounded-lg bg-slate-950 border border-slate-800 px-4 py-2.5 text-sm text-slate-200 placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
            />
          </div>

          <button
            type="button"
            onClick={submit}
            disabled={busy}
            className="w-full mt-5 inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed text-slate-950 font-semibold py-3 transition"
          >
            {busy ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4" />
                Run my Health Check
              </>
            )}
          </button>

          {error && (
            <div className="mt-3 rounded-lg bg-rose-950/40 border border-rose-800/60 px-4 py-2.5 text-sm text-rose-300">
              {error}
            </div>
          )}

          {rejected.length > 0 && (
            <div className="mt-3 rounded-lg bg-amber-950/40 border border-amber-800/60 px-4 py-2.5 text-xs text-amber-300">
              Could not resolve {rejected.length} row(s). Check spelling of NSE symbols.
            </div>
          )}

          <p className="mt-5 text-[11px] text-slate-500 text-center">
            For information only. Not investment advice. Your data is used to generate this audit
            and is not sold to third parties.
          </p>
        </div>

        <div className="grid grid-cols-3 gap-4 mt-12 text-center">
          <div>
            <div className="text-3xl font-bold text-emerald-400">6</div>
            <div className="text-xs text-slate-400 mt-1">analysts per stock</div>
          </div>
          <div>
            <div className="text-3xl font-bold text-emerald-400">2k+</div>
            <div className="text-xs text-slate-400 mt-1">backtested decisions</div>
          </div>
          <div>
            <div className="text-3xl font-bold text-emerald-400">100%</div>
            <div className="text-xs text-slate-400 mt-1">provenance-tracked</div>
          </div>
        </div>
      </div>
    </div>
  );
}
